import React, { createContext, useContext } from 'react';
import { create } from 'zustand';

export type LibraryTagType = {
  id: string | number;
  name: string;
  color?: string;
  isSystem?: boolean;
  version?: number;
};

export type FolderTagsMap = {
  [folderId: string]: LibraryTagType[];
};

export type LibraryTagsStore = {
  tags: LibraryTagType[];
  folderTags: FolderTagsMap;
  setTags: (tags: LibraryTagType[]) => void;
  updateFolderTags: (folderId: string, tags: LibraryTagType[]) => void;
};

export const useLibraryTagsStore = create<LibraryTagsStore>((set) => ({
  tags: [],
  folderTags: {},
  setTags: (tags) => set({ tags }),
  updateFolderTags: (folderId, tags) =>
    set((state) => ({
      folderTags: {
        ...state.folderTags,
        [folderId]: tags,
      },
    })),
}));

const LibraryTagsStoreContext = createContext<ReturnType<typeof useLibraryTagsStore> | null>(null);

export const LibraryTagsStoreProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const store = useLibraryTagsStore();
  return (
    <LibraryTagsStoreContext.Provider value={store}>{children}</LibraryTagsStoreContext.Provider>
  );
};

export const useLibraryTagsStoreContext = () => {
  const context = useContext(LibraryTagsStoreContext);
  if (!context) {
    throw new Error('useLibraryTagsStoreContext must be used within a LibraryTagsStoreProvider');
  }
  return context;
};

LibraryTagsStoreProvider.displayName = 'LibraryTagsStoreProvider';
